import {useEffect, useState, useMemo, useCallback, useRef, memo} from 'react';
import {api} from '../lib/auth.js';

/**
 * A spreadsheet over one master-data resource. Kunder, Leverantörer and Artiklar each
 * get their own grid — they have disjoint field sets, and one table holding all three
 * would be mostly empty cells.
 *
 * WHY IT IS FAST: only the ACTIVE cell is an <input>. Every other cell is text. Forty
 * customers times twenty-eight columns is 1 120 cells, and mounting that many controlled
 * inputs — each with its own onChange closure — is what makes a React grid crawl on
 * every keystroke. Rows are memoised and re-render only when their own data, their dirty
 * state, or the active cell's membership in that row changes, so typing repaints one row
 * rather than the table.
 *
 * Edits are held locally until saved. The alternative — a write per keystroke or per
 * cell — would mean one Fortnox call per cell, and Fortnox rate-limits; it would also
 * make an accidental keystroke a change to the live accounting register.
 *
 * Saving sends only the changed cells of the changed rows, through the same allow-list
 * and mirror the single-record editor uses.
 */

const RESOURCES = [
	['customers', 'Kunder'], ['suppliers', 'Leverantörer'], ['articles', 'Artiklar'],
];

const LABELS = {
	Name: 'Namn', OrganisationNumber: 'Orgnr', Email: 'E-post', Phone1: 'Telefon',
	Phone2: 'Telefon 2', Address1: 'Adress', Address2: 'Adress 2', ZipCode: 'Postnr',
	City: 'Ort', Country: 'Land', CountryCode: 'Landskod', Currency: 'Valuta',
	VATNumber: 'Momsnr', VATType: 'Momstyp', TermsOfPayment: 'Betalvillkor', Type: 'Typ',
	Active: 'Aktiv', Comments: 'Kommentar', YourReference: 'Er referens',
	OurReference: 'Vår referens', PriceList: 'Prislista', EmailInvoice: 'E-fakturering',
	Description: 'Benämning', SalesPrice: 'Försäljningspris', PurchasePrice: 'Inköpspris',
	Unit: 'Enhet', SalesAccount: 'Försäljningskonto', VAT: 'Moms', Note: 'Anteckning',
	SupplierNumber: 'Leverantörsnr', StockGoods: 'Lagervara', WebshopArticle: 'Webbshop',
	BankAccountNumber: 'Bankkonto', DeliveryName: 'Leveransnamn',
	DeliveryAddress1: 'Leveransadress', DeliveryZipCode: 'Leveranspostnr',
	DeliveryCity: 'Leveransort', WayOfDelivery: 'Leveranssätt',
	InvoiceRemark: 'Fakturatext', Housework: 'ROT/RUT', EAN: 'EAN',
	Manufacturer: 'Tillverkare', ManufacturerArticleNumber: 'Tillverkarens artnr',
	PreDefinedAccount: 'Förvalt konto', CostCenter: 'Kostnadsställe',
};

const show = v => (v == null || v === '' ? '' : typeof v === 'boolean' ? (v ? 'ja' : 'nej') : String(v));

/**
 * A row. `memo` here is not a micro-optimisation: without it every keystroke re-renders
 * all forty rows and the grid stops feeling like a spreadsheet.
 */
const Row = memo(function Row({row, columns, edits, activeCol, onActivate}) {
	return (
		<tr className={edits ? 'g__row--dirty' : undefined}>
			<th scope="row" className="g__id">
				{row.id}
				{row.pending && <span className="pill" title="Väntar på att skickas till Fortnox">kö</span>}
				{row.source === 'fortnox' && <span className="pill" title="Hämtad från Fortnox, ingen ström satt">fnx</span>}
			</th>
			{columns.map(c => {
				const dirty = edits && c.name in edits;
				const value = dirty ? edits[c.name] : row.cells[c.name];
				return (
					<td key={c.name}
						className={`g__c${dirty ? ' g__c--dirty' : ''}${activeCol === c.name ? ' g__c--active' : ''}`}
						tabIndex={-1}
						onMouseDown={() => onActivate(row.id, c.name)}>
						{show(value)}
					</td>
				);
			})}
		</tr>
	);
});

export default function Grid() {
	const [resource, setResource] = useState('customers');
	const [data, setData] = useState(null);
	const [error, setError] = useState(null);
	const [edits, setEdits] = useState({});          // {rowId: {col: value}}
	const [active, setActive] = useState(null);      // {row, col}
	const [q, setQ] = useState('');
	const [saving, setSaving] = useState(false);
	const [result, setResult] = useState(null);
	const inputRef = useRef(null);

	const load = useCallback(async () => {
		setData(null); setError(null); setEdits({}); setActive(null); setResult(null);
		try {
			const r = await api(`/api/grid/${resource}`);
			const j = await r.json();
			if (!r.ok) throw new Error(j.error);
			setData(j);
		} catch (e) { setError(e.message); }
	}, [resource]);
	useEffect(() => { load(); }, [load]);

	// Filtering is over the RENDERED value, so searching for "ja" finds active records
	// and searching for a postcode finds it wherever it sits.
	const rows = useMemo(() => {
		if (!data) return [];
		if (!q.trim()) return data.rows;
		const needle = q.trim().toLowerCase();
		return data.rows.filter(r => r.id.toLowerCase().includes(needle)
			|| Object.values(r.cells).some(v => show(v).toLowerCase().includes(needle)));
	}, [data, q]);

	const dirtyRows = Object.keys(edits).filter(id => Object.keys(edits[id]).length);
	const dirtyCells = dirtyRows.reduce((n, id) => n + Object.keys(edits[id]).length, 0);

	const setCell = useCallback((rowId, col, value) => {
		setEdits(e => {
			const row = {...(e[rowId] ?? {})};
			row[col] = value;
			return {...e, [rowId]: row};
		});
	}, []);

	/** Revert one cell rather than leaving a no-op edit marked as dirty. */
	const clearCell = useCallback((rowId, col) => {
		setEdits(e => {
			if (!e[rowId] || !(col in e[rowId])) return e;
			const row = {...e[rowId]};
			delete row[col];
			const next = {...e};
			if (Object.keys(row).length) next[rowId] = row; else delete next[rowId];
			return next;
		});
	}, []);

	const activate = useCallback((row, col) => setActive({row, col}), []);

	/** Excel keys: arrows move, Enter commits down, Tab commits right, Escape reverts. */
	const move = useCallback((dRow, dCol) => {
		if (!active || !data) return;
		const ri = rows.findIndex(r => r.id === active.row);
		const ci = data.columns.findIndex(c => c.name === active.col);
		const nr = Math.min(Math.max(ri + dRow, 0), rows.length - 1);
		const nc = Math.min(Math.max(ci + dCol, 0), data.columns.length - 1);
		if (rows[nr]) setActive({row: rows[nr].id, col: data.columns[nc].name});
	}, [active, data, rows]);

	const onKey = useCallback(e => {
		if (!active) return;
		if (e.key === 'Escape') { clearCell(active.row, active.col); setActive(null); return; }
		if (e.key === 'Enter') { e.preventDefault(); move(e.shiftKey ? -1 : 1, 0); return; }
		if (e.key === 'Tab') { e.preventDefault(); move(0, e.shiftKey ? -1 : 1); return; }
		// Arrows only navigate when the caret is not being used to move inside the text.
		if (e.key === 'ArrowDown') { e.preventDefault(); move(1, 0); }
		if (e.key === 'ArrowUp') { e.preventDefault(); move(-1, 0); }
	}, [active, clearCell, move]);

	useEffect(() => { inputRef.current?.focus(); inputRef.current?.select(); }, [active]);

	const save = async () => {
		setSaving(true); setResult(null);
		try {
			const r = await api(`/api/grid/${resource}`, {
				method: 'PUT', headers: {'Content-Type': 'application/json'},
				body: JSON.stringify({edits: dirtyRows.map(id => ({id, changes: edits[id]}))}),
			});
			const j = await r.json();
			if (!r.ok) { setResult({error: j.error}); return; }
			setResult(j);
			// Only the rows that actually saved are cleared; a failed row keeps its edit so
			// the user can fix it rather than retype it.
			const ok = new Set(j.saved.map(s => s.id));
			setEdits(e => Object.fromEntries(Object.entries(e).filter(([id]) => !ok.has(id))));
			if (j.saved.length) {
				const fresh = await (await api(`/api/grid/${resource}`)).json();
				setData(fresh);
			}
		} catch (e) { setResult({error: e.message}); } finally { setSaving(false); }
	};

	const activeRow = active && rows.find(r => r.id === active.row);
	const activeColSpec = active && data?.columns.find(c => c.name === active.col);
	const activeValue = active
		? (edits[active.row]?.[active.col] ?? activeRow?.cells[active.col] ?? '')
		: '';

	return (
		<>
			<p className="sub">
				Hela registret i en tabell, redigerbart direkt i cellerna. Ändringar sparas när
				du trycker Spara — inte per tangenttryck, eftersom varje rad är ett anrop till
				Fortnox. Piltangenter, Tab och Enter flyttar sig; Esc ångrar cellen.
			</p>

			<div className="toolbar">
				<span className="grain">
					<span className="grain__label">Register</span>
					{RESOURCES.map(([key, label]) => (
						<button key={key} className="seg" aria-pressed={resource === key}
							onClick={() => setResource(key)}>{label}</button>
					))}
				</span>
				<input className="search" placeholder="Sök i alla fält…" value={q}
					onChange={e => setQ(e.target.value)} />
				<span className="stamp">
					{rows.length} rader{q ? ` av ${data?.rows.length ?? 0}` : ''}
					{dirtyCells ? ` · ${dirtyCells} ändrade celler på ${dirtyRows.length} rader` : ''}
				</span>
				<span className="spacer" />
				<button className="btn" onClick={load} disabled={saving}>Läs om</button>
				<button className="btn btn--primary" onClick={save} disabled={!dirtyCells || saving}>
					{saving ? 'Sparar…' : `Spara ${dirtyCells || ''}`}
				</button>
			</div>

			{result && (
				<div className="panel">
					<div className="panel__title">
						{result.error ? 'Kunde inte spara' : `${result.saved.length} av ${result.attempted} rader sparade`}
					</div>
					<p>
						{result.error ?? (result.failed?.length
							? result.failed.map(f => `${f.id}: ${f.error}${f.queued ? ' (köad för nästa synk)' : ''}`).join(' · ')
							: 'Allt skrivet till Fortnox och speglat lokalt.')}
					</p>
				</div>
			)}

			{error && <div className="state">{error}</div>}
			{!data && !error && <div className="state">Hämtar…</div>}

			{data && !data.writable && (
				<div className="panel"><b>Skrivskyddad</b>
					<p>Scopet saknas i Fortnox-token, så tabellen går att läsa men inte spara.</p></div>
			)}

			{data && (
				<div className="table-wrap g__wrap" onKeyDown={onKey}>
					<table className="g" style={{minWidth: 200 + data.columns.length * 130}}>
						<thead>
							<tr>
								<th className="g__id">{data.idField}</th>
								{data.columns.map(c => (
									<th key={c.name} title={c.name}>
										{LABELS[c.name] ?? c.name}{c.required && <i title="Obligatoriskt"> *</i>}
									</th>
								))}
							</tr>
						</thead>
						<tbody>
							{rows.map(r => (
								<Row key={r.id} row={r} columns={data.columns} edits={edits[r.id]}
									activeCol={active?.row === r.id ? active.col : null}
									onActivate={activate} />
							))}
						</tbody>
					</table>

					{/* One input for the whole grid, positioned over the active cell by the
					    browser's own focus handling. Mounting an input per cell is what makes
					    spreadsheet-shaped React slow. */}
					{active && activeColSpec && (
						<div className="g__editor">
							<span className="g__el">{active.row} · {LABELS[active.col] ?? active.col}</span>
							{activeColSpec.boolean ? (
								<select ref={inputRef} className="search g__in"
									value={activeValue === true || activeValue === 'ja' ? 'ja' : 'nej'}
									onChange={e => setCell(active.row, active.col, e.target.value === 'ja')}>
									<option value="ja">ja</option><option value="nej">nej</option>
								</select>
							) : (
								<input ref={inputRef} className="search g__in" value={activeValue ?? ''}
									onChange={e => setCell(active.row, active.col, e.target.value)} />
							)}
							<button className="btn btn--sm" onClick={() => clearCell(active.row, active.col)}>
								Ångra cell
							</button>
							<button className="btn btn--sm" onClick={() => setActive(null)}>Stäng</button>
						</div>
					)}
				</div>
			)}

			<p className="foot-note">
				Tabellen läses ur den lokala spegeln, inte ur Fortnox — det är därför den öppnas
				direkt i stället för att göra ett anrop per rad. Spegeln hålls sann av synken.
				Sparning går åt andra hållet: varje ändrad rad skickas till Fortnox, och bara de
				fält du faktiskt rört. Rader som Fortnox nekar behåller sin ändring här så att
				den går att rätta i stället för att skrivas om.
			</p>
		</>
	);
}
